import React from "react";
import { Box, Grid, Typography, alpha } from "@mui/material";
import type { GridSize } from "@mui/material/Grid";
import type { SxProps, Theme } from "@mui/material/styles";

export interface IDetailInfoField {
  /** Small label shown above the value (e.g. "Guest Name"). */
  label: string;
  /** Value shown in bold beneath the label. */
  value: React.ReactNode;
  /** Leading icon rendered in a tinted rounded square. */
  icon?: React.ReactNode;
}

interface IDetailInfoCardProps {
  /** Fields to render as icon + label/value tiles. */
  fields: IDetailInfoField[];
  /** Optional heading shown above the grid (e.g. "Lead Details"). */
  title?: string;
  /** Responsive columns per item. Defaults to 2 / 3 / 4 per row. */
  columns?: { xs?: GridSize; sm?: GridSize; md?: GridSize; lg?: GridSize };
  /** Extra styles merged onto the outer card Box. */
  sx?: SxProps<Theme>;
}

/**
 * A compact, icon-led key/value summary card used to present read-only detail
 * fields (lead / payment summaries). Each field renders a tinted icon square,
 * a muted label, and a bold value — driven entirely by the `fields` prop so it
 * stays generic across screens.
 */
export const DetailInfoCard: React.FC<IDetailInfoCardProps> = ({
  fields,
  title,
  columns = { xs: 6, sm: 4, md: 3 },
  sx,
}) => {
  return (
    <Box
      sx={{
        borderRadius: 2.4,
        border: "1px solid",
        borderColor: "divider",
        borderLeft: "3px solid",
        borderLeftColor: "primary.main",
        p: 1,
        ...sx,
      }}
    >
      {title && (
        <Typography
          variant="subtitle2"
          fontWeight={700}
          color="primary.main"
          sx={{ mb: 1 }}
        >
          {title}
        </Typography>
      )}
      <Grid container spacing={1} rowSpacing={1.25}>
        {fields.map((field) => (
          <Grid
            key={field.label}
            item
            xs={columns.xs}
            sm={columns.sm}
            md={columns.md}
            lg={columns.lg}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              {field.icon && (
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 30,
                    height: 30,
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                    color: "primary.main",
                  }}
                >
                  {field.icon}
                </Box>
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", fontSize: "0.7rem", lineHeight: 1.2 }}
                >
                  {field.label}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="text.primary"
                  noWrap
                  sx={{ lineHeight: 1.3 }}
                >
                  {field.value}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
